import { useLazyQuery } from '@apollo/client';
import { Button, Col, Row, Tag } from 'antd';
import dayjs from 'dayjs';
import { useEffect, useState } from 'react';
import { DeleteIcon, EditIcon } from '../../assets/svg';
import { MODULES, defaultDateFormat } from '../../common/constants';
import TableComponent from '../../components/CommonTable';
import DeleteConfirmModal from '../../components/DeleteConfirmModal';
import Portal from '../../components/Portal';
import SearchComponent from '../../components/SearchComponent';
import AddEditModal from './AddEditModal';
import { DELETE_PRODUCT_PACKAGING } from './graphql/Mutations';
import { PRODUCT_PACKAGINGS } from './graphql/Queries';
import './productPackaging.less';

const ProductPackaging = () => {
  const initialUserFilter = {
    filters: { skip: 0, limit: 10 },
    sort: { sortOn: 'createdAt', sortBy: 'DESC' },
  };

  const initialPaginationValue = {
    total: 0,
    current: 1,
  };

  const [paginationProp, setPaginationProp] = useState(initialPaginationValue);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedRecord, setSelectedRecord] = useState();
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [sortedInfo, setSortedInfo] = useState({});
  const [userFilter, setUserFilter] = useState(initialUserFilter);

  const [getProductPackagingList, { loading, data }] = useLazyQuery(PRODUCT_PACKAGINGS, {
    fetchPolicy: 'network-only',
    onCompleted: (res) => {
      const pagination = {
        ...paginationProp,
        defaultPageSize: 10,
        total: res?.productPackagings?.count,
      };
      setPaginationProp(pagination);
    },
    onError() { },
  });

  useEffect(() => {
    getProductPackagingList({ variables: userFilter });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const refetchProductPackagingDetails = () => {
    setSortedInfo({});
    setUserFilter(initialUserFilter);
    setPaginationProp(initialPaginationValue);
    getProductPackagingList({ variables: { ...initialUserFilter } });
  };

  const handleTableChange = (pagination, filter, sorter) => {
    const { current } = pagination;
    const skip = (current - 1) * pagination.pageSize;
    setSortedInfo(sorter);
    setPaginationProp({ ...paginationProp, ...pagination });
    if (sorter?.column) {
      setUserFilter({
        ...userFilter,
        sort: {
          sortOn: sorter.field,
          sortBy: sorter.order === 'ascend' ? 'ASC' : 'DESC',
        },
      });
      getProductPackagingList({
        variables: {
          ...userFilter,
          filters: {
            skip,
            limit: pagination.pageSize,
          },
          sort: {
            sortOn: sorter.field,
            sortBy: sorter.order === 'ascend' ? 'ASC' : 'DESC',
          },
        },
      });
    } else {
      setUserFilter({
        ...userFilter,
        filters: { skip, limit: pagination.pageSize },
        sort: { sortOn: 'createdAt', sortBy: 'DESC' },
      });
      getProductPackagingList({
        variables: {
          ...userFilter,
          filters: {
            skip,
            limit: pagination.pageSize,
          },
          sort: { sortOn: 'createdAt', sortBy: 'DESC' },
        },
      });
    }
  };

  const renderPackagingBindis = (packagingBindis) => {
    if (!packagingBindis || packagingBindis.length === 0) return '-';

    return (
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: '4px' }}>
        {packagingBindis.map((bindi) => (
          <Tag key={`${bindi.key}-${bindi.qty}`} color="blue" style={{ marginBottom: '4px' }}>
            {bindi.key}: {bindi.qty}
          </Tag>
        ))}
      </div>
    );
  };

  const onSearchChange = async (value) => {
    setUserFilter({ ...userFilter, skip: 0, search: value });
    getProductPackagingList({
      variables: {
        ...userFilter,
        filters: {
          skip: 0,
          search: value,
        },
      },
    });
  };

  const columns = [
    {
      title: '#',
      key: '#',
      width: '10%',
      align: 'center',
      render: (record, _, index) => (
        <div>{userFilter?.filters?.skip + index + 1}</div>
      ),
    },
    {
      title: 'Product Name',
      dataIndex: 'productName',
      key: 'productName',
      sorter: true,
      sortOrder: sortedInfo?.columnKey === 'productName' && sortedInfo?.order,
      render: (text) => text || '-',
    },
    {
      title: 'Quantity',
      dataIndex: 'quantity',
      key: 'quantity',
      sorter: true,
      sortOrder: sortedInfo?.columnKey === 'quantity' && sortedInfo?.order,
      render: (text) => text || '-',
    },
    {
      title: 'Packaging Bindis',
      dataIndex: 'packagingBindis',
      key: 'packagingBindis',
      render: renderPackagingBindis,
      width: 300,
    },
    {
      title: 'Created By',
      dataIndex: 'createdBy',
      key: 'createdBy',
      render: (createdBy) => {
        if (!createdBy) return '-';
        return `${createdBy.firstName || ''} ${createdBy.lastName || ''}`.trim();
      },
    },
    {
      title: 'Created At',
      dataIndex: 'createdAt',
      key: 'createdAt',
      align: 'createdAt',
      sorter: true,
      sortOrder: sortedInfo?.columnKey === 'createdAt' && sortedInfo?.order,
      render: (date) => dayjs(date).format(defaultDateFormat),
    },
    {
      align: 'center',
      render: (id, record) => (
        <div className="d-flex align-center">
          <EditIcon
            className="mr-10 pointer"
            onClick={(e) => {
              e.stopPropagation();
              setSelectedRecord(record);
              setIsModalOpen(true);
            }}
          />
          <DeleteIcon
            className="pointer"
            onClick={(e) => {
              e.stopPropagation();
              setSelectedRecord(record);
              setShowDeleteModal(true);
            }}
          />
        </div>
      ),
    },
  ];

  return (
    <div className="categories-module">
      <Portal portalId="header-wrapper">
        <h1 className="m-0 font-600">{MODULES.PRODUCT_PACKAGING}</h1>
      </Portal>
      <Row className="mb-24" justify="space-between">
        <Col>
          <SearchComponent
            id="search-container-id"
            name="product-packaging"
            getData={onSearchChange}
            className="category-search search-component"
          />
        </Col>
        <Col>
          <Button
            type="primary"
            className="add-new-btn"
            onClick={() => setIsModalOpen(true)}
          >
            Add New
          </Button>
        </Col>
      </Row>
      <TableComponent
        loadingData={loading}
        columns={columns}
        data={data?.productPackagings?.productPackagings}
        onChange={handleTableChange}
        paginationConfig={paginationProp}
        rowKey={(obj) => obj?.id}
      />
      {isModalOpen && (
        <AddEditModal
          isModalOpen={isModalOpen}
          setIsModalOpen={setIsModalOpen}
          refetch={refetchProductPackagingDetails}
          selectedRecord={selectedRecord}
          setSelectedRecord={setSelectedRecord}
        />
      )}
      {showDeleteModal && (
        <DeleteConfirmModal
          isModalOpen={showDeleteModal}
          setIsModalOpen={setShowDeleteModal}
          refetch={refetchProductPackagingDetails}
          selectedRecord={selectedRecord}
          setSelectedRecord={setSelectedRecord}
          mutation={DELETE_PRODUCT_PACKAGING}
          module="product-packaging"
        />
      )}
    </div>
  );
};

export default ProductPackaging;